import { Head, Link, router } from "@inertiajs/react"
import {
  Archive,
  ArchiveRestore,
  Building2,
  Download,
  Edit,
  FileText,
  Mail,
  MonitorSmartphone,
  Trash2,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { adminUsersPath } from "@/routes"
import type { BreadcrumbItem, PermissionCatalog } from "@/types"

interface Assignment {
  id: number
  role: string
  position: number
  property_id: number
  property_name: string
  suite_id: number | null
  suite_number: string | null
}

interface UserSession {
  id: string
  user_agent: string | null
  ip_address: string | null
  created_at: string
}

interface Brochure {
  id: number
  label: string
  status: string
  ready: boolean
  property_id: number
  property_name: string
  suite_number: string
  created_at: string
  download_url: string | null
}

interface Props {
  user: {
    id: number
    name: string
    email: string
    verified: boolean
    archived: boolean
    role: { id: number; name: string; description: string | null }
    profile_kind: "broker" | "agent" | null
    permissions: string[]
    created_at: string
  }
  assignments: Assignment[]
  brochures: Brochure[]
  sessions: UserSession[]
  permission_catalog: PermissionCatalog
  can: { update: boolean; archive: boolean; destroy: boolean }
}

const SLOT_LABELS: Record<string, string> = {
  leasing_contact: "Leasing contact",
  listing_broker: "Listing broker",
  presented_by: "Presented by",
}

export default function Show({
  user,
  assignments,
  brochures,
  sessions,
  permission_catalog,
  can,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Users", href: adminUsersPath() },
    { title: user.name, href: `/admin/users/${user.id}` },
  ]

  const held = new Set(user.permissions)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={user.name} />

      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{user.name}</h1>
              <Badge variant="secondary" className="capitalize">
                {user.role.name}
              </Badge>
              {user.archived && <Badge variant="outline">Archived</Badge>}
              {!user.verified && (
                <Badge variant="destructive">Unverified</Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              {user.email} · added {user.created_at}
            </p>
            {user.archived && (
              <p className="text-muted-foreground text-sm">
                Archived accounts can&apos;t sign in and hold no permissions.
                Assignments below are preserved.
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() =>
                router.post(
                  `/admin/users/${user.id}/resend_invitation`,
                  {},
                  { preserveScroll: true },
                )
              }
            >
              <Mail className="mr-2 h-4 w-4" /> Resend Setup
            </Button>
            {can.update && (
              <Button render={<Link href={`/admin/users/${user.id}/edit`} />}>
                <Edit className="mr-2 h-4 w-4" /> Edit
              </Button>
            )}
            {can.archive &&
              (user.archived ? (
                <Button
                  variant="outline"
                  onClick={() =>
                    router.patch(
                      `/admin/users/${user.id}/restore`,
                      {},
                      { preserveScroll: true },
                    )
                  }
                >
                  <ArchiveRestore className="mr-2 h-4 w-4" /> Restore
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (
                      confirm(
                        `Archive ${user.email}? They'll be signed out immediately.`,
                      )
                    ) {
                      router.patch(
                        `/admin/users/${user.id}/archive`,
                        {},
                        { preserveScroll: true },
                      )
                    }
                  }}
                >
                  <Archive className="mr-2 h-4 w-4" /> Archive
                </Button>
              ))}
            {can.destroy && (
              <Button
                variant="outline"
                onClick={() => {
                  if (
                    confirm(
                      `Permanently delete ${user.email} and their assignments?`,
                    )
                  ) {
                    router.delete(`/admin/users/${user.id}`)
                  }
                }}
              >
                <Trash2 className="text-destructive mr-2 h-4 w-4" /> Delete
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4" /> Property Assignments (
                {assignments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {assignments.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Not assigned to any property. Assign from a property&apos;s
                  page.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Slot</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <Link
                            href={`/admin/properties/${a.property_id}`}
                            className="font-medium hover:underline"
                          >
                            {a.property_name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {a.suite_number
                            ? `Suite ${a.suite_number}`
                            : "Property-wide"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {SLOT_LABELS[a.role] ?? a.role}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MonitorSmartphone className="h-4 w-4" /> Active Sessions (
                {sessions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Not signed in anywhere.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Device</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Started</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="max-w-[18rem] truncate text-sm">
                          {s.user_agent ?? "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {s.ip_address ?? "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {s.created_at}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {user.profile_kind === "broker" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" /> Brochures Generated (
                {brochures.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {brochures.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Hasn&apos;t generated any co-branded brochures yet.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      <TableHead>Suite</TableHead>
                      <TableHead>Style</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">PDF</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {brochures.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell className="font-medium">
                          {b.property_name}
                        </TableCell>
                        <TableCell>{b.suite_number}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {b.label}
                        </TableCell>
                        <TableCell>
                          <Badge variant={b.ready ? "default" : "secondary"}>
                            {b.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {b.download_url && (
                            <Button
                              size="sm"
                              variant="ghost"
                              render={<a href={b.download_url} />}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">
                What they can do
                <span className="text-muted-foreground ml-2 text-sm font-normal">
                  via the {user.role.name} role
                </span>
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                render={<Link href={`/admin/roles/${user.role.id}`} />}
              >
                View role
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {held.size === 0 ? (
              <p className="text-muted-foreground text-sm">
                No permissions — this account can sign in but reach nothing.
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(permission_catalog).map(
                  ([category, groups]) => {
                    const labels = Object.values(groups)
                      .flatMap((group) => Object.entries(group))
                      .filter(([key]) => held.has(key))
                      .map(([, label]) => label)

                    if (labels.length === 0) return null

                    return (
                      <div key={category} className="space-y-1">
                        <p className="text-xs font-semibold tracking-wide uppercase">
                          {category}
                        </p>
                        <ul className="text-muted-foreground space-y-0.5 text-sm">
                          {labels.map((label) => (
                            <li key={label}>{label}</li>
                          ))}
                        </ul>
                      </div>
                    )
                  },
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
